import {NoteIcon} from '@primer/octicons-react'
import React from 'react'

export type Command = {
  leadingVisual: React.ReactNode
  name: string
  prompt: string
}

export function getCommands(contextType?: string): Command[] {
  if (!contextType) {
    return []
  }

  if (contextType === 'pull-request') {
    return [
      {
        leadingVisual: React.createElement(NoteIcon),
        name: 'Analyze this pull request',
        prompt: `Use the following info about this pull request:
- File changes
- Pull request description
- Pull request actions job logs
- Pull request reviews
- Pull request comments
- Pull request issue comments

Make sure follow the below rules to learn about comments:
- Comments are referring to both pull request comments and its issue comments.
- Pull request description and file changes are necessary context to learn about the comments.
- Pull request comments belong to the same coversation thread if one replies to another. Consider the entire conversation thread to learn about those comments.
- Pull request issue comments are individual review comments not part of any conversation thread.

And then provide answers to the following questions in separate paragraphs:
- A concise representation and reasoning of the file changes.
- Whether the pull request description reflects the purpose of the file changes. Please suggest possible improvements and present them with examples.
- Whether there is any complexities or notable efforts, such as handling edge cases or reducing technical debt.
- When there was any failure in the actions job logs, suggest possible fixes with examples. Otherwise, ignore this question.

Do not repeat information and synthesize the changes in a conversational and succinct manner.`,
      },
      {
        leadingVisual: React.createElement(NoteIcon),
        name: 'Review this pull request',
        prompt: `Use the following info about this pull request:
- File changes
- Pull request description
- Pull request reviews
- Pull request comments
- Pull request issue comments

Make sure follow the below rules to learn about comments:
- Comments are referring to both pull request comments and its issue comments.
- Pull request description and file changes are necessary context to learn about the comments.
- Pull request comments belong to the same coversation thread if one replies to another. Consider the entire conversation thread to learn about those comments.
- Pull request issue comments are individual review comments not part of any conversation thread.

And then provide answers to the following questions in separate paragraphs:
- Provide insights on the scope and purpose of this pull request.
- Analyze the file changes and suggest risk areas that reviewers should focus on.
- Summarize the existing reviews and comments. Please also highlight unresolved conversations that reviewers should pay attention to.

Do not repeat information and synthesize the changes in a conversational and succinct manner.`,
      },
      {
        leadingVisual: React.createElement(NoteIcon),
        name: 'Explain this pull request',
        prompt: `Use the following info about this pull request:
- File changes
- Pull request description
- Pull request reviews
- Pull request comments
- Pull request issue comments

Make sure follow the below rules to learn about comments:
- Comments are referring to both pull request comments and its issue comments.
- Pull request description and file changes are necessary context to learn about the comments.
- Pull request comments belong to the same coversation thread if one replies to another. Consider the entire conversation thread to learn about those comments.
- Pull request issue comments are individual review comments not part of any conversation thread.

And then provide answers to the following questions in separate paragraphs:
- Provide a high-level overview of the purpose of this pull request.
- Summarize the impact and potential risks for the file changes of this pull request.
- Indicate whether this pull request has gotten any approval and has all review comments addressed.

Do not repeat information and synthesize the changes in a conversational and succinct manner.`,
      },
      {
        leadingVisual: React.createElement(NoteIcon),
        name: 'Catch me up on my reviews',
        prompt: `Use the following info about this pull request:
- File changes
- Pull request description
- Pull request comments
- Pull request issue comments

Make sure follow the below rules to learn about comments:
- Comments are referring to both pull request comments and its issue comments.
- Pull request description and file changes are necessary context to learn about the comments.
- Pull request comments belong to the same coversation thread if one replies to another. Consider the entire conversation thread to learn about those comments.
- Pull request issue comments are individual review comments not part of any conversation thread.

And then, with this context, please follow these instructions:
1. If I did not leave any comment on this pull request:
  - Simply state that no comment was left by me and do not provide any further analysis.
2. If I did leave comments:
  - In the first paragraph, explain whether all of my review comments have been fully resolved.
  - In the second paragraph, detail any unresolved review comments from me, including the reason why they remain unresolved.

Keep your analysis succinct and conversational. Do not repeat context details or include extra phrases like “yes, you did leave comments.” Synthesize the information clearly without unnecessary repetition.`,
      },
    ]
  }

  return []
}
